import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Buildings, ArrowLeft, Users, TreeStructure, FlowArrow, Cpu, Clock, Plus, Trash, Database, Pencil, UserPlus, UploadSimple, MagnifyingGlass
} from "@phosphor-icons/react";
import { RoleFormDialog, DepartmentFormDialog, ProcessFormDialog, SystemFormDialog } from "@/components/admin/EntityForms";
import BulkUserUploadDialog from "@/components/admin/BulkUserUploadDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

export default function AdminPanel() {
  const navigate = useNavigate();
  const { user, logout, isSuperAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState("users");
  const [activityLog, setActivityLog] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  
  // Data states
  const [users, setUsers] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [seedDialogOpen, setSeedDialogOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  
  // Form dialog states
  const [roleFormOpen, setRoleFormOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [deptFormOpen, setDeptFormOpen] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [processFormOpen, setProcessFormOpen] = useState(false);
  const [editingProcess, setEditingProcess] = useState(null);
  const [systemFormOpen, setSystemFormOpen] = useState(false);
  const [editingSystem, setEditingSystem] = useState(null);

  // Invite user dialog
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteUser, setInviteUser] = useState({ name: "", email: "", password: "", role: "admin", is_super_admin: false, training_role: "employee", department: "" });
  const [inviting, setInviting] = useState(false);

  // Bulk user upload dialog
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);

  // Users search
  const [usersSearch, setUsersSearch] = useState("");
  const filteredUsers = useMemo(() => {
    const q = usersSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.department || "").toLowerCase().includes(q) ||
      (u.training_role || "").toLowerCase().includes(q) ||
      (u.role || "").toLowerCase().includes(q)
    );
  }, [users, usersSearch]);

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, historyRes, rolesRes, deptsRes, procsRes, sysRes] = await Promise.all([
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/auth/login-history`, { withCredentials: true }),
        axios.get(`${API}/roles`, { withCredentials: true }),
        axios.get(`${API}/departments`, { withCredentials: true }),
        axios.get(`${API}/processes`, { withCredentials: true }),
        axios.get(`${API}/systems`, { withCredentials: true }),
      ]);
      
      setUsers(usersRes.data);
      setLoginHistory(historyRes.data);
      setRoles(rolesRes.data);
      setDepartments(deptsRes.data);
      setProcesses(procsRes.data);
      setSystems(sysRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired");
        logout();
      } else if (error.response?.status === 403) {
        toast.error("Access denied");
        navigate("/dashboard");
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  }, [logout, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update user role
  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.patch(`${API}/users/${userId}/role`, { role: newRole }, { withCredentials: true });
      toast.success("User role updated");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  // Update training role
  const handleTrainingRoleChange = async (userId, newRole) => {
    try {
      await axios.patch(`${API}/users/${userId}/training-role`, { training_role: newRole }, { withCredentials: true });
      toast.success("Training role updated");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update training role");
    }
  };

  // Toggle super admin flag
  const handleSuperAdminToggle = async (u, value) => {
    const action = value ? "grant" : "revoke";
    if (!window.confirm(`${value ? "Grant" : "Revoke"} super admin rights ${value ? "to" : "from"} ${u.name || u.email}?`)) {
      fetchData(); // reset the switch
      return;
    }
    try {
      await axios.patch(
        `${API}/users/${u.user_id}/super-admin`,
        { is_super_admin: value },
        { withCredentials: true }
      );
      toast.success(`Super admin ${action}ed`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} super admin`);
      fetchData();
    }
  };

  // Invite (pre-create) user
  const handleInviteUser = async () => {
    const name = inviteUser.name.trim();
    const email = inviteUser.email.trim().toLowerCase();
    const password = inviteUser.password;
    if (!name || !email || !password) {
      toast.error("Name, email and password are required");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setInviting(true);
    try {
      const created = await axios.post(
        `${API}/users/invite`,
        {
          name,
          email,
          password,
          role: inviteUser.is_super_admin ? "admin" : inviteUser.role,
          training_role: inviteUser.training_role,
          department: inviteUser.department || "",
        },
        { withCredentials: true }
      );
      if (inviteUser.is_super_admin) {
        try {
          await axios.patch(
            `${API}/users/${created.data.user_id}/super-admin`,
            { is_super_admin: true },
            { withCredentials: true }
          );
        } catch (err) {
          toast.error(err.response?.data?.detail || "Added user but failed to mark as super admin");
        }
      }
      toast.success(`Added ${email}. Share the password with them so they can sign in.`);
      setInviteDialogOpen(false);
      setInviteUser({ name: "", email: "", password: "", role: "admin", is_super_admin: false, training_role: "employee", department: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add user");
    } finally {
      setInviting(false);
    }
  };

  // Module Permissions dialog
  const [permsTarget, setPermsTarget] = useState(null);  // user being edited
  const [permsState, setPermsState] = useState(null);    // { map:{view,edit}, canteen:..., training:..., admin:... }
  const [permsSaving, setPermsSaving] = useState(false);

  const openPermissions = (u) => {
    const ea = u.effective_access || {};
    setPermsState({
      map: { view: !!ea.map?.view, edit: !!ea.map?.edit },
      canteen: { view: !!ea.canteen?.view, edit: !!ea.canteen?.edit },
      training: { view: !!ea.training?.view, edit: !!ea.training?.edit },
      admin: { view: !!ea.admin?.view, edit: !!ea.admin?.edit },
      inventory: { view: !!ea.inventory?.view, edit: !!ea.inventory?.edit },
    });
    setPermsTarget(u);
  };

  const savePermissions = async () => {
    if (!permsTarget) return;
    setPermsSaving(true);
    try {
      await axios.patch(
        `${API}/users/${permsTarget.user_id}/module-access`,
        { module_access: permsState },
        { withCredentials: true }
      );
      toast.success(`Permissions updated for ${permsTarget.name || permsTarget.email}`);
      setPermsTarget(null);
      setPermsState(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save permissions");
    } finally {
      setPermsSaving(false);
    }
  };

  // Load activity log (lazy)
  const loadActivity = async () => {
    setActivityLoading(true);
    try {
      const res = await axios.get(`${API}/activity-log?limit=200`, { withCredentials: true });
      setActivityLog(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load activity");
    } finally {
      setActivityLoading(false);
    }
  };

  // Delete user (super admin only)
  const handleDeleteUser = async (u) => {
    if (!window.confirm(`Remove ${u.name || u.email}?\nThey will lose access immediately and all their sessions will be invalidated.`)) return;
    try {
      const res = await axios.delete(`${API}/users/${u.user_id}`, { withCredentials: true });
      toast.success(res.data?.message || `Removed ${u.name || u.email}`);
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || "Failed to remove user";
      toast.error(msg, { duration: 6000 });
      // eslint-disable-next-line no-console
      console.error("Delete user failed:", error.response?.status, msg);
    }
  };

  // Seed data
  const handleSeedData = async () => {
    setSeeding(true);
    try {
      const response = await axios.post(`${API}/seed`, {}, { withCredentials: true });
      toast.success(`Data seeded: ${response.data.departments} departments, ${response.data.roles} roles, ${response.data.systems} systems, ${response.data.processes} processes`);
      setSeedDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to seed data");
    } finally {
      setSeeding(false);
    }
  };

  // Delete item
  const handleDelete = async (type, id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    
    try {
      await axios.delete(`${API}/${type}/${id}`, { withCredentials: true });
      toast.success("Item deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };

  // Edit handlers
  const handleEditRole = (role) => {
    setEditingRole(role);
    setRoleFormOpen(true);
  };

  const handleEditDept = (dept) => {
    setEditingDept(dept);
    setDeptFormOpen(true);
  };

  const handleEditProcess = (proc) => {
    setEditingProcess(proc);
    setProcessFormOpen(true);
  };

  const handleEditSystem = (sys) => {
    setEditingSystem(sys);
    setSystemFormOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary" data-testid="admin-loading">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-panel">
      {/* Header */}
      <header className="glass-header border-b border-black/10 sticky top-0 z-50">
        <div className="flex items-center justify-between px-4 md:px-6 h-16">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="back-to-home">
              <ArrowLeft size={18} className="mr-2" />
              Home
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-md flex items-center justify-center">
                <Buildings size={18} className="text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold">Admin Panel</h1>
                <p className="text-xs text-muted-foreground">Manage organization data</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {isSuperAdmin && (
              <Button onClick={() => setSeedDialogOpen(true)} variant="outline" size="sm" data-testid="seed-data-btn">
                <Database size={16} className="mr-2" />
                Seed Data
              </Button>
            )}
            <Badge variant="secondary">{user?.email}</Badge>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="p-4 md:p-6 max-w-7xl mx-auto">
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === "activity" && activityLog.length === 0) loadActivity(); }}>
          <TabsList className="mb-6 bg-secondary/50">
            <TabsTrigger value="users" className="gap-2" data-testid="admin-tab-users">
              <Users size={16} /> Users ({users.length})
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2" data-testid="admin-tab-history">
              <Clock size={16} /> Login History
            </TabsTrigger>
            <TabsTrigger value="activity" className="gap-2" data-testid="admin-tab-activity">
              <Clock size={16} /> Activity Log
            </TabsTrigger>
            <TabsTrigger value="roles" className="gap-2" data-testid="admin-tab-roles">
              <TreeStructure size={16} /> Roles ({roles.length})
            </TabsTrigger>
            <TabsTrigger value="departments" className="gap-2" data-testid="admin-tab-departments">
              <Buildings size={16} /> Departments ({departments.length})
            </TabsTrigger>
            <TabsTrigger value="processes" className="gap-2" data-testid="admin-tab-processes">
              <FlowArrow size={16} /> Processes ({processes.length})
            </TabsTrigger>
            <TabsTrigger value="systems" className="gap-2" data-testid="admin-tab-systems">
              <Cpu size={16} /> Systems ({systems.length})
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            {isSuperAdmin && (
              <div className="mb-4 flex flex-wrap justify-between items-center gap-3">
                <p className="text-sm text-muted-foreground">
                  Create accounts for your team. Users sign in with the email + password you set — no Google account required.
                </p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={() => setBulkUploadOpen(true)} data-testid="bulk-upload-btn">
                    <UploadSimple size={16} className="mr-2" />
                    Bulk Upload (Excel/CSV)
                  </Button>
                  <Button onClick={() => setInviteDialogOpen(true)} data-testid="invite-user-btn">
                    <UserPlus size={16} className="mr-2" />
                    Add User
                  </Button>
                </div>
              </div>
            )}
            <div className="bg-white rounded-md border shadow-sm">
              <div className="p-3 border-b">
                <div className="relative">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={usersSearch}
                    onChange={(e) => setUsersSearch(e.target.value)}
                    placeholder={`Search ${users.length} users by name, email, department or role...`}
                    className="pl-9"
                    data-testid="users-search"
                  />
                </div>
              </div>
              <ScrollArea className="h-[calc(100vh-380px)]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>App Role</TableHead>
                      <TableHead>Training Role</TableHead>
                      <TableHead>Super Admin</TableHead>
                      <TableHead>Joined</TableHead>
                      {isSuperAdmin && <TableHead>Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.length === 0 ? (
                      <TableRow><TableCell colSpan={isSuperAdmin ? 7 : 6} className="text-center text-muted-foreground py-6">No users match "{usersSearch}"</TableCell></TableRow>
                    ) : filteredUsers.map((u) => {
                      const isSelf = u.user_id === user?.user_id;
                      return (
                        <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                          <TableCell className="font-medium">{u.name}</TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell>
                            {isSuperAdmin && !isSelf ? (
                              <Select
                                value={u.role}
                                onValueChange={(value) => handleRoleChange(u.user_id, value)}
                              >
                                <SelectTrigger className="w-32" data-testid={`role-select-${u.user_id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="viewer">Viewer</SelectItem>
                                  <SelectItem value="editor">Editor</SelectItem>
                                  <SelectItem value="admin">Admin</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <Badge variant={u.role === "admin" ? "default" : "secondary"}>{u.role}</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {isSuperAdmin && !isSelf ? (
                              <Select
                                value={u.training_role || "employee"}
                                onValueChange={(value) => handleTrainingRoleChange(u.user_id, value)}
                              >
                                <SelectTrigger className="w-40" data-testid={`training-role-select-${u.user_id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="employee">Employee</SelectItem>
                                  <SelectItem value="department_head">Dept. Head</SelectItem>
                                  <SelectItem value="training_admin">Training Admin</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <Badge variant="outline">{(u.training_role || "employee").replace("_", " ")}</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {isSuperAdmin && !isSelf ? (
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={!!u.is_super_admin}
                                  onCheckedChange={(v) => handleSuperAdminToggle(u, v)}
                                  data-testid={`super-admin-switch-${u.user_id}`}
                                />
                                {u.is_super_admin && (
                                  <Badge className="bg-accent text-accent-foreground text-xs">Super Admin</Badge>
                                )}
                              </div>
                            ) : u.is_super_admin ? (
                              <Badge className="bg-accent text-accent-foreground">Super Admin</Badge>
                            ) : null}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">{formatDate(u.created_at)}</TableCell>
                          {isSuperAdmin && (
                            <TableCell>
                              <div className="flex gap-2 items-center">
                                {!isSelf && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openPermissions(u)}
                                    data-testid={`permissions-user-${u.user_id}`}
                                    title="Set module permissions"
                                  >
                                    Permissions
                                  </Button>
                                )}
                                {!isSelf && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDeleteUser(u)}
                                    className="text-destructive hover:bg-destructive/10 hover:text-destructive border-destructive/30"
                                    data-testid={`delete-user-${u.user_id}`}
                                    title="Delete user"
                                  >
                                    <Trash size={14} className="mr-1" />
                                    Delete
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          </TabsContent>

          {/* Login History Tab */}
          <TabsContent value="history">
            <div className="bg-white rounded-md border shadow-sm">
              <ScrollArea className="h-[calc(100vh-280px)]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Login Time</TableHead>
                      <TableHead>IP Address</TableHead>
                      <TableHead>User Agent</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loginHistory.map((h) => (
                      <TableRow key={h.history_id} data-testid={`login-history-${h.history_id}`}>
                        <TableCell className="font-medium">{h.name}</TableCell>
                        <TableCell>{h.email}</TableCell>
                        <TableCell>{formatDate(h.login_at)}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{h.ip_address || "-"}</TableCell>
                        <TableCell className="text-muted-foreground text-xs max-w-xs truncate">{h.user_agent || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          </TabsContent>

          {/* Activity Log Tab */}
          <TabsContent value="activity">
            <div className="flex justify-between items-center mb-3">
              <p className="text-sm text-muted-foreground">All user activity — logins, downloads, attempts. Last 200 events.</p>
              <Button variant="outline" size="sm" onClick={loadActivity} disabled={activityLoading} data-testid="refresh-activity">
                {activityLoading ? "Loading..." : "Refresh"}
              </Button>
            </div>
            <div className="bg-white rounded-md border shadow-sm">
              <ScrollArea className="h-[calc(100vh-320px)]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Target</TableHead>
                      <TableHead>Details</TableHead>
                      <TableHead>IP</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activityLog.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No activity yet. Click Refresh.</TableCell></TableRow>
                    ) : activityLog.map((a) => (
                      <TableRow key={a.log_id} data-testid={`activity-row-${a.log_id}`}>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(a.created_at)}</TableCell>
                        <TableCell className="font-medium">{a.name || a.email}<div className="text-xs text-muted-foreground">{a.email}</div></TableCell>
                        <TableCell><Badge variant="secondary">{a.action}</Badge></TableCell>
                        <TableCell className="text-xs">{a.target_type ? `${a.target_type}: ${a.target_id?.slice(0, 16)}` : "—"}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-xs truncate">
                          {a.details && Object.keys(a.details).length > 0 ? Object.entries(a.details).map(([k, v]) => `${k}=${v}`).join(" • ") : "—"}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{a.ip_address || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          </TabsContent>
          <TabsContent value="roles">
            <div className="mb-4">
              <Button onClick={() => { setEditingRole(null); setRoleFormOpen(true); }} data-testid="create-role-btn">
                <Plus size={16} className="mr-2" /> Create Role
              </Button>
            </div>
            <div className="bg-white rounded-md border shadow-sm">
              <ScrollArea className="h-[calc(100vh-320px)]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Reports To</TableHead>
                      <TableHead>KPIs</TableHead>
                      <TableHead>Software</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {roles.map((r) => (
                      <TableRow key={r.role_id} data-testid={`role-row-${r.role_id}`}>
                        <TableCell className="font-medium">{r.title}</TableCell>
                        <TableCell><Badge variant="outline">{r.department}</Badge></TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {r.reports_to ? roles.find(ro => ro.role_id === r.reports_to)?.title || "-" : "Top Level"}
                        </TableCell>
                        <TableCell>{r.kpis?.length || 0}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {r.software_used?.slice(0, 2).map((s, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">{s}</Badge>
                            ))}
                            {r.software_used?.length > 2 && <Badge variant="secondary" className="text-xs">+{r.software_used.length - 2}</Badge>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEditRole(r)} data-testid={`edit-role-${r.role_id}`}>
                              <Pencil size={16} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete("roles", r.role_id)}>
                              <Trash size={16} className="text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          </TabsContent>

          {/* Departments Tab */}
          <TabsContent value="departments">
            <div className="mb-4">
              <Button onClick={() => { setEditingDept(null); setDeptFormOpen(true); }} data-testid="create-dept-btn">
                <Plus size={16} className="mr-2" /> Create Department
              </Button>
            </div>
            <div className="bg-white rounded-md border shadow-sm">
              <ScrollArea className="h-[calc(100vh-320px)]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Systems</TableHead>
                      <TableHead>Bottlenecks</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {departments.map((d) => (
                      <TableRow key={d.department_id} data-testid={`dept-row-${d.department_id}`}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.color }} />
                            {d.name}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-xs truncate text-muted-foreground text-sm">{d.description}</TableCell>
                        <TableCell>{d.system_touchpoints?.length || 0}</TableCell>
                        <TableCell>{d.bottlenecks?.length || 0}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEditDept(d)} data-testid={`edit-dept-${d.department_id}`}>
                              <Pencil size={16} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete("departments", d.department_id)}>
                              <Trash size={16} className="text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          </TabsContent>

          {/* Processes Tab */}
          <TabsContent value="processes">
            <div className="mb-4">
              <Button onClick={() => { setEditingProcess(null); setProcessFormOpen(true); }} data-testid="create-process-btn">
                <Plus size={16} className="mr-2" /> Create Process
              </Button>
            </div>
            <div className="bg-white rounded-md border shadow-sm">
              <ScrollArea className="h-[calc(100vh-320px)]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Steps</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {processes.map((p) => (
                      <TableRow key={p.process_id} data-testid={`process-row-${p.process_id}`}>
                        <TableCell className="font-medium">{p.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{departments.find(d => d.department_id === p.department_id)?.name || p.department_id}</Badge>
                        </TableCell>
                        <TableCell>{p.steps?.length || 0}</TableCell>
                        <TableCell>{p.estimated_duration || "-"}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEditProcess(p)} data-testid={`edit-process-${p.process_id}`}>
                              <Pencil size={16} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete("processes", p.process_id)}>
                              <Trash size={16} className="text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          </TabsContent>

          {/* Systems Tab */}
          <TabsContent value="systems">
            <div className="mb-4">
              <Button onClick={() => { setEditingSystem(null); setSystemFormOpen(true); }} data-testid="create-system-btn">
                <Plus size={16} className="mr-2" /> Create System
              </Button>
            </div>
            <div className="bg-white rounded-md border shadow-sm">
              <ScrollArea className="h-[calc(100vh-320px)]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Used By</TableHead>
                      <TableHead>URL</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {systems.map((s) => (
                      <TableRow key={s.system_id} data-testid={`system-row-${s.system_id}`}>
                        <TableCell className="font-medium">{s.name}</TableCell>
                        <TableCell><Badge variant="secondary">{s.type}</Badge></TableCell>
                        <TableCell>{s.used_by_roles?.length || 0} roles</TableCell>
                        <TableCell>
                          {s.url && s.url !== "#" ? (
                            <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm">
                              Open
                            </a>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEditSystem(s)} data-testid={`edit-system-${s.system_id}`}>
                              <Pencil size={16} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete("systems", s.system_id)}>
                              <Trash size={16} className="text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Permissions Dialog */}
      <Dialog open={!!permsTarget} onOpenChange={(o) => { if (!o) { setPermsTarget(null); setPermsState(null); } }}>
        <DialogContent data-testid="permissions-dialog">
          <DialogHeader>
            <DialogTitle>Module Permissions</DialogTitle>
            <DialogDescription>
              {permsTarget && (<>For <strong>{permsTarget.name || permsTarget.email}</strong>. Toggle what they can view & edit per module.</>)}
            </DialogDescription>
          </DialogHeader>
          {permsState && (
            <div className="space-y-3">
              {[
                { key: "map", label: "Operating Map" },
                { key: "canteen", label: "Canteen" },
                { key: "training", label: "Training Intelligence" },
                { key: "inventory", label: "Inventory Management" },
                { key: "admin", label: "Admin Panel" },
              ].map((m) => (
                <div key={m.key} className="flex items-center justify-between p-3 border rounded-md">
                  <div>
                    <p className="font-medium text-sm">{m.label}</p>
                    <p className="text-xs text-muted-foreground">module key: <code>{m.key}</code></p>
                  </div>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Switch
                        checked={permsState[m.key].view}
                        onCheckedChange={(v) => setPermsState({ ...permsState, [m.key]: { view: v, edit: v ? permsState[m.key].edit : false } })}
                        data-testid={`perm-${m.key}-view`}
                      />
                      View
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <Switch
                        checked={permsState[m.key].edit}
                        disabled={!permsState[m.key].view}
                        onCheckedChange={(v) => setPermsState({ ...permsState, [m.key]: { ...permsState[m.key], edit: v } })}
                        data-testid={`perm-${m.key}-edit`}
                      />
                      Edit
                    </label>
                  </div>
                </div>
              ))}
              <p className="text-xs text-muted-foreground">Edit requires View. Super admins always have full access regardless.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPermsTarget(null); setPermsState(null); }}>Cancel</Button>
            <Button onClick={savePermissions} disabled={permsSaving} data-testid="save-permissions-btn">
              {permsSaving ? "Saving..." : "Save Permissions"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Seed Data Dialog */}
      <Dialog open={seedDialogOpen} onOpenChange={setSeedDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Seed Organization Data</DialogTitle>
            <DialogDescription>
              This will populate the database with CME Envirosystems organization data including departments, roles, processes, and systems.
              <br /><br />
              <strong className="text-destructive">Warning:</strong> This will replace all existing data.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSeedDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSeedData} disabled={seeding} data-testid="confirm-seed-btn">
              {seeding ? "Seeding..." : "Seed Data"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk User Upload Dialog */}
      <BulkUserUploadDialog
        open={bulkUploadOpen}
        onOpenChange={setBulkUploadOpen}
        onImported={fetchData}
      />

      {/* Invite User Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent data-testid="invite-user-dialog">
          <DialogHeader>
            <DialogTitle>Add User</DialogTitle>
            <DialogDescription>
              Create a new user with email & password. They will sign in with these credentials.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <div>
              <Label>Full Name</Label>
              <Input
                value={inviteUser.name}
                onChange={(e) => setInviteUser({ ...inviteUser, name: e.target.value })}
                placeholder="e.g. Sneha Patil"
                data-testid="invite-user-name"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={inviteUser.email}
                onChange={(e) => setInviteUser({ ...inviteUser, email: e.target.value })}
                placeholder="user@company.com"
                data-testid="invite-user-email"
              />
            </div>
            <div>
              <Label>Initial Password (min 6 chars)</Label>
              <Input
                type="text"
                value={inviteUser.password}
                onChange={(e) => setInviteUser({ ...inviteUser, password: e.target.value })}
                placeholder="Set a temporary password"
                data-testid="invite-user-password"
              />
              <p className="text-xs text-muted-foreground mt-1">Share this with the user; they can change it later (P2).</p>
            </div>
            <div>
              <Label>Department</Label>
              <Input
                value={inviteUser.department}
                onChange={(e) => setInviteUser({ ...inviteUser, department: e.target.value })}
                placeholder="e.g. Production"
                data-testid="invite-user-department"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>App Role</Label>
                <Select value={inviteUser.role} onValueChange={(v) => setInviteUser({ ...inviteUser, role: v })} disabled={inviteUser.is_super_admin}>
                  <SelectTrigger data-testid="invite-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewer">Viewer</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                    <SelectItem value="admin">Admin (full RW)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Training Role</Label>
                <Select value={inviteUser.training_role} onValueChange={(v) => setInviteUser({ ...inviteUser, training_role: v })}>
                  <SelectTrigger data-testid="invite-user-training-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">Employee / Trainee</SelectItem>
                    <SelectItem value="department_head">Department Head</SelectItem>
                    <SelectItem value="training_admin">Training Admin / HR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-accent/10 border rounded-md">
              <div>
                <Label className="text-sm font-medium">Grant Super Admin</Label>
                <p className="text-xs text-muted-foreground">Full system control — can manage other users & seed data</p>
              </div>
              <Switch
                checked={inviteUser.is_super_admin}
                onCheckedChange={(v) => setInviteUser({ ...inviteUser, is_super_admin: v })}
                data-testid="invite-super-admin-switch"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleInviteUser} disabled={inviting} data-testid="confirm-invite-user">
              {inviting ? "Adding..." : "Add User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Form Dialogs */}
      <RoleFormDialog
        open={roleFormOpen}
        onOpenChange={setRoleFormOpen}
        role={editingRole}
        roles={roles}
        departments={departments}
        systems={systems}
        onSave={fetchData}
      />

      <DepartmentFormDialog
        open={deptFormOpen}
        onOpenChange={setDeptFormOpen}
        department={editingDept}
        roles={roles}
        systems={systems}
        onSave={fetchData}
      />

      <ProcessFormDialog
        open={processFormOpen}
        onOpenChange={setProcessFormOpen}
        process={editingProcess}
        departments={departments}
        processes={processes}
        onSave={fetchData}
      />

      <SystemFormDialog
        open={systemFormOpen}
        onOpenChange={setSystemFormOpen}
        system={editingSystem}
        roles={roles}
        onSave={fetchData}
      />
    </div>
  );
}
